/*
* Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
*/
import React, {
  ChangeEvent,
  CSSProperties,
  useEffect,
  useState
} from 'react';
import Tabs from '@material-ui/core/Tabs';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {useExtendStyles} from '../../hooks/useExtendStyles';
import {dlsTabIndicatorBorder, toggleThemeStyle} from '../../theme/helpers';
import {DLSTheme} from '../../types/types';
import {TabIndicatorProps} from './current/TabIndicator';
import Tab from './Tab';
import styles from './TabView.module.scss';

interface StyleProps {
  backgroundColor?: string;
};

export interface TabPropTypes extends StyleProps {
  // `indicator` renders in the current theme only - see current/TabIndicator.tsx ...
  tabItems: {tabHeader: string, path?: string, tag?: string, indicator?: TabIndicatorProps}[],
  onChange: (event: ChangeEvent<{}>, value: any) => void,
  onClose?: (event: ChangeEvent<{}>, value: any) => void,
  value?: number,
  disabled?: boolean,
  reInitialize?: boolean,
  customStyles?: boolean,
  minWidth?: number,
  maxWidth?: number,
  centered?: boolean,
  view?: __esri.MapView;
  style?: CSSProperties;
  overrides?: (theme: DLSTheme) => GenericObject;
  variant?: 'scrollable' | 'fullWidth' | 'standard';
  customTabs?: boolean;
  isCondensedVariant?: boolean;
  closeForFirstTab?: boolean;
  passSelected?: boolean;
  urgentNotifications?: any;
  allNotifications?: any;
};

const useStyles = () => ({
  // current theme: superseded by Tab.tsx's own per-tab border-bottom
  // (drawn on each tab's own 48px-tall root) - MUI's global sliding
  // TabIndicator here never rendered reliably (its containing block was
  // never anchored to anything predictable), so it's hidden rather than
  // positioned. Legacy theme is untouched, still using this indicator ...
  customTabIndicator : toggleThemeStyle({
    backgroundColor : dlsColor('black')
  }, {
    bottom          : '4px',
    backgroundColor : 'transparent'
  }),
  tabRoot : {
  },
  // current theme's inter-tab spacing lives here (rather than as root's own
  // paddingLeft, see current-theme Tab.tsx) so each tab's root is exactly as
  // wide as its pill background - MUI measures the selected tab's root to
  // position/size the indicator, so any extra width on root beyond the
  // pill's own would misalign the two. Also fills the gaps between tabs
  // left by each tab's own border-bottom, the same way current/TabView.tsx's
  // flexContainer does - height/boxSizing match Tab.tsx's root exactly so
  // the two borders don't leave a hairline gap at the seam ...
  tabContainer : toggleThemeStyle({}, {
    gap          : '16px',
    height       : '48px',
    boxSizing    : 'border-box',
    borderBottom : dlsTabIndicatorBorder('resting')
  }),
  endAdornment : {
    position : 'absolute',
    right    : 0
  }
});

/**
 * @TabView
 *
 * Defines the Tabs component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 **
 *  <TabView tabItems={tabs} onChange={handleChange} value={value} />
 *
 * ```
 *
 */
export const TabView = ({
  tabItems,
  onChange,
  onClose,
  allNotifications,
  urgentNotifications,
  reInitialize,
  customStyles,
  minWidth,
  maxWidth,
  value,
  disabled,
  view,
  overrides,
  style,
  passSelected = false,
  centered = true,
  customTabs = false,
  closeForFirstTab = false,
  isCondensedVariant = false,
  variant = 'fullWidth'
}: TabPropTypes) => {
  const tabViewStyles                       = useExtendStyles(useStyles, overrides);
  const classes                             = tabViewStyles();
  const [initializeTabs, setInitializeTabs] = useState(false);

  useEffect(() => {
    if (view) {
      const listener = view?.popup.watch('visible', function() {
        if (view?.popup.visible) {
          setInitializeTabs(true);
        }
        return function() {
          listener?.remove();
        };
      });
    } else {
      setInitializeTabs(true);
    }
  }, []);

  useEffect(() => {
    setInitializeTabs(true);
  }, [reInitialize]);

  return (
    <div className={customStyles ? styles.tabContainerAlt : styles.tabContainer} style={style}>
      {initializeTabs && (
        <Tabs value={value}
          disabled={disabled}
          onChange={onChange}
          indicatorColor='secondary'
          textColor='secondary'
          classes={{
            indicator     : classes.customTabIndicator,
            flexContainer : classes.tabContainer,
            root          : classes.tabRoot,
            scroller      : classes?.scroller
          }}
          variant={variant}
          centered={centered}>
          {tabItems?.map((tab, index) => {
            const condition = ((index !== 0 || closeForFirstTab) && customTabs);
            const props = {
              selected : passSelected ? value === index : undefined
            };
            return (
              <Tab index={index}
                tab={tab}
                onClick={onChange}
                showToolTip={condition}
                showIcon={condition}
                maxWidth={maxWidth}
                minWidth={minWidth}
                customTabs={customTabs}
                customStyles={customStyles}
                allNotifications={allNotifications}
                urgentNotifications={urgentNotifications}
                isCondensedVariant={isCondensedVariant}
                overrides={overrides}
                onClose={onClose}
                {...props} />
            );
          })}
        </Tabs>
      )}
    </div>
  );
};

export default TabView;
